import { useCallback } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { defineMessages, useIntl } from 'react-intl';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import {
	parseIssueType,
	parseUser,
	parseUsers,
} from '@atlassian/jira-platform-fields/fieldParsers.tsx';

import {
	ISSUE_TYPE_FIELD_ID,
	PARENT_FIELD_ID,
	REPORTER_FIELD_ID,
	USER_PICKER_FIELD_TYPES,
} from '../../common/constants';
import { FieldProcessor } from '../../utils/FieldProcessor';
import type { DraftWorkItem } from '../use-ai-create-project-context/types';

import type { useValidateIssuesToCreateQuery } from './__generated__/useValidateIssuesToCreateQuery.graphql';
import type {
	FieldConfigurationLike,
	IssueType,
	IssueTypeLike,
	IssueTypeToFieldsConfiguration,
	ProjectLike,
} from './types';

export type JiraDraftDataForCreateAllCommandResult = {
	success: boolean;
	context: {
		draftWorkItems: DraftWorkItem[];
		issueTypeToFieldsConfigMap: IssueTypeToFieldsConfiguration;
	};
	error?: string;
};

// Exported for testing purposes
export const messages: {
	missingRequiredField: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	noWorkItemsToCreate: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	invalidFieldHierarchy: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	missingRequiredField: {
		id: 'jira-create-work-items.validation.missing-required-field',
		defaultMessage: 'Field ‘{fieldName}’ is required for type ‘{issueTypeName}’',
		description: 'The message displayed when a required field is missing.',
	},
	noWorkItemsToCreate: {
		id: 'jira-create-work-items.validation.no-work-items-to-create',
		defaultMessage: 'No work items to create',
		description: 'The message displayed when there are no work items to create.',
	},
	invalidFieldHierarchy: {
		id: 'jira-create-work-items.validation.invalid-field-hierarchy',
		defaultMessage: 'Invalid work type hierarchy for type ‘{issueTypeName}’',
		description: 'The message displayed when the field hierarchy is invalid.',
	},
});

const isEmpty = (value: unknown) =>
	value == null || // catches null / undefined
	String(value).trim() === '' || // catches '' and whitespace
	(Array.isArray(value) && value.length === 0) ||
	(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

/**
 * Converts a fields configuration GraphQL connection into a record mapping field IDs to their corresponding field configuration GraphQL node.
 *
 * @param fieldsConfiguration - The fields configuration object, which may be undefined.
 * @returns A record where each key is a field ID and each value is the corresponding field configuration.
 */
function toFieldConfigurationNode(
	fieldsConfiguration: IssueTypeLike['fieldsConfiguration'] | undefined,
): Record<string, FieldConfigurationLike> {
	if (!fieldsConfiguration) {
		return {};
	}

	return Object.fromEntries(
		(fieldsConfiguration?.edges ?? [])
			.filter(Boolean)
			.map((edge) => [edge?.node?.fieldId, edge?.node]),
	);
}

/**
 * Converts an `IssueTypeLike` object to an `IssueType` object.
 *
 * @param issueTypeLike - The source object containing issue type information.
 * @returns An `IssueType` object with mapped properties
 */
function toIssueType(issueTypeLike: IssueTypeLike): IssueType {
	return {
		issueTypeId: String(issueTypeLike.issueTypeId),
		name: issueTypeLike.name,
		hierarchyLevel: issueTypeLike.hierarchy?.level ?? 0,
		avatarUrl: issueTypeLike.avatar?.small ?? undefined,
		fieldsConfigurationNode: toFieldConfigurationNode(issueTypeLike.fieldsConfiguration),
	};
}

/**
 * Builds a mapping of issue type IDs to their corresponding field configuration GraphQL nodes for a given project.
 * Iterates over the project's issue types and constructs an object where each key is an issue type ID,
 * and each value is the result of transforming the issue type node using `toIssueType`.
 *
 * @param project - The project GraphQL node containing issue types, or `null`/`undefined`.
 * @returns An object mapping issue type IDs to their field configurations.
 */
function buildIssueTypeToFieldsConfiguration(
	project: ProjectLike | null | undefined,
): IssueTypeToFieldsConfiguration {
	const issueTypeToFieldsConfiguration: IssueTypeToFieldsConfiguration = {};

	project?.issueTypes?.edges?.forEach((issueTypeEdge) => {
		const issueTypeNode = issueTypeEdge?.node;
		const issueTypeId = issueTypeNode?.issueTypeId;
		if (!issueTypeId) {
			return;
		}

		issueTypeToFieldsConfiguration[issueTypeId] = toIssueType(issueTypeNode);
	});

	return issueTypeToFieldsConfiguration;
}

/**
 * Validates the draft work items that are being sent for issue creation. Checks:
 * 1) Required fields are present. Returns with success: true if all required fields are present,
 * otherwise returns with success: false and the error.
 * 2) LLM-hallucinated fields are removed. Will always return with success: true and the draft work
 * items with the LLM-hallucinated fields removed.
 *
 * Returns undefined if the issuetype to fields config map is not available
 *
 * @param {DraftWorkItem[]} draftWorkItems - The draft work items to validate.
 * @returns {JiraDraftDataForCreateAllCommandResult} - The validation result.
 */
export const useValidateIssuesToCreate = ({
	cloudId,
	projectIdOrKey,
	sendAifcAnalyticsEvent,
}: {
	cloudId: string;
	projectIdOrKey: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): {
	validateIssuesToCreate: (
		draftWorkItemsOriginal: DraftWorkItem[],
	) => Promise<JiraDraftDataForCreateAllCommandResult | undefined>;
} => {
	const { formatMessage } = useIntl();
	const environment = useRelayEnvironment();

	const validateIssuesToCreate = useCallback(
		async (
			draftWorkItemsOriginal: DraftWorkItem[],
		): Promise<JiraDraftDataForCreateAllCommandResult | undefined> => {
			const draftWorkItems = cloneDeep(draftWorkItemsOriginal);

			const queryData = await fetchQuery<useValidateIssuesToCreateQuery>(
				environment,
				graphql`
					query useValidateIssuesToCreateQuery($cloudId: ID!, $projectIdOrKey: String!) {
						jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
							issueTypes {
								edges {
									node {
										issueTypeId
										name
										hierarchy {
											level
										}
										avatar {
											small
										}

										fieldsConfiguration @optIn(to: "JiraIssueTypeFieldsConfiguration") {
											edges {
												node {
													__typename
													type
													fieldId
													name

													... on JiraIssueFieldConfiguration {
														fieldConfig {
															isRequired
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				`,
				{
					cloudId,
					projectIdOrKey,
				},
				{ fetchPolicy: 'network-only' },
			).toPromise();

			const issueTypeToFieldsConfigMap = buildIssueTypeToFieldsConfiguration(
				queryData?.jira_projectByIdOrKey,
			);

			if (!issueTypeToFieldsConfigMap) {
				sendAifcAnalyticsEvent?.({
					action: 'failed',
					actionSubject: 'validateIssuesToCreate',
					attributes: { reason: 'noIssueTypeToFieldsConfigMap' },
				});
				return undefined;
			}

			if (draftWorkItems.length === 0) {
				sendAifcAnalyticsEvent?.({
					action: 'failed',
					actionSubject: 'validateIssuesToCreate',
					attributes: { reason: 'noDraftWorkItems' },
				});
				return {
					success: false,
					context: {
						draftWorkItems,
						issueTypeToFieldsConfigMap,
					},
					error: formatMessage(messages.noWorkItemsToCreate),
				};
			}

			const allDraftWorkItems = [
				...draftWorkItems,
				...draftWorkItems.flatMap((parent) => parent.childWorkItems || []),
			];

			for (const draftWorkItem of allDraftWorkItems) {
				const { issueTypeId, fields } = draftWorkItem;

				const fieldConfig = issueTypeToFieldsConfigMap[issueTypeId].fieldsConfigurationNode;
				const validFieldIds = new Set(Object.keys(fieldConfig));
				const requiredFields = Object.values(fieldConfig).filter(
					(field) => field.fieldConfig?.isRequired,
				);

				// Remove any LLM-hallucinated fields from the draft work item
				// Skip parent field, since AGG field config can hide it
				const fieldIdsToRemove = Object.keys(fields).filter(
					(fieldId) =>
						(!validFieldIds.has(fieldId) || isEmpty(fields[fieldId])) &&
						fieldId !== PARENT_FIELD_ID,
				);
				for (const fieldId of fieldIdsToRemove) {
					delete draftWorkItem.fields[fieldId];
				}

				// Transform fields to fit creation payload
				const fieldProcessor = new FieldProcessor();
				for (const fieldId of Object.keys(draftWorkItem.fields)) {
					if (USER_PICKER_FIELD_TYPES.includes(fieldConfig[fieldId]?.__typename)) {
						if (Array.isArray(draftWorkItem.fields[fieldId])) {
							draftWorkItem.fields[fieldId] = parseUsers(draftWorkItem.fields[fieldId]).map(
								(userWithWrapper) => userWithWrapper?.user?.accountId || null,
							);
						} else {
							draftWorkItem.fields[fieldId] =
								parseUser(draftWorkItem.fields[fieldId])?.user?.accountId || null;
						}
					}

					if (fieldId === ISSUE_TYPE_FIELD_ID) {
						draftWorkItem.fields[fieldId] = parseIssueType(draftWorkItem.fields[fieldId]);
					}

					draftWorkItem.fields[fieldId] = fieldProcessor.populateFieldPayload(
						fieldConfig[fieldId] ? fieldConfig[fieldId].type : 'parent',
						fieldId,
						draftWorkItem.fields[fieldId],
					);
				}

				// Check if any child work items have child work items themselves
				const { childWorkItems } = draftWorkItem;
				const hasNestedChildren = childWorkItems?.some(
					(child: DraftWorkItem & { childInvocationIds?: string[] }) =>
						child.childInvocationIds?.length,
				);
				if (hasNestedChildren) {
					sendAifcAnalyticsEvent?.({
						action: 'nestedChildWorkItems',
						actionSubject: 'validateIssuesToCreate',
						attributes: {},
					});
				}
				// Check that all child work items have a hierarchy level that is 1 less than the parent's
				const parentHierarchyLevel = issueTypeToFieldsConfigMap[issueTypeId].hierarchyLevel;
				const isInvalidFieldHierarchy = childWorkItems?.some(
					(child: DraftWorkItem) =>
						issueTypeToFieldsConfigMap[child.issueTypeId].hierarchyLevel !==
						parentHierarchyLevel - 1,
				);

				if (isInvalidFieldHierarchy) {
					return {
						success: false,
						error: formatMessage(messages.invalidFieldHierarchy, {
							issueTypeName: issueTypeToFieldsConfigMap[issueTypeId].name,
						}),
						context: {
							draftWorkItems,
							issueTypeToFieldsConfigMap,
						},
					};
				}
				// Check all required fields are present for issue creation
				const seenFieldIds = new Set(Object.keys(draftWorkItem.fields));
				for (const requiredField of requiredFields) {
					// Specifically handle the reporter field, since when it's not passed to the Create API
					// it defaults to the current user
					if (requiredField.fieldId === REPORTER_FIELD_ID) {
						if (draftWorkItem.fields[REPORTER_FIELD_ID] === null) {
							delete draftWorkItem.fields[REPORTER_FIELD_ID];
						}
						continue;
					}

					if (!seenFieldIds.has(requiredField.fieldId)) {
						sendAifcAnalyticsEvent?.({
							action: 'failed',
							actionSubject: 'validateIssuesToCreate',
							attributes: {
								reason: 'missingRequiredField',
								fieldType: requiredField.__typename,
								nativeIssueTypeName:
									['Story', 'Task', 'Bug', 'Epic', 'Initiative', 'Sub-task'].find(
										(type) => type === issueTypeToFieldsConfigMap[issueTypeId].name,
									) || '__other',
							},
						});
						return {
							success: false,
							error: formatMessage(messages.missingRequiredField, {
								fieldName: requiredField.name,
								issueTypeName: issueTypeToFieldsConfigMap[issueTypeId].name,
							}),
							context: {
								draftWorkItems,
								issueTypeToFieldsConfigMap,
							},
						};
					}
				}
			}

			return {
				success: true,
				context: {
					draftWorkItems,
					issueTypeToFieldsConfigMap,
				},
			};
		},
		[formatMessage, sendAifcAnalyticsEvent, cloudId, environment, projectIdOrKey],
	);

	return { validateIssuesToCreate };
};
